/**
 * ¿Se puede leer esta imagen?
 *
 * Es lo que el Figma llama "Detección automática", con las pastillas de **Luz** y **Nitidez**
 * sobre el vídeo. Aquí vive el cálculo; el motor de visión no hace falta para esto.
 *
 * ── EL UMBRAL ES UNA DECISIÓN SOCIAL, NO TÉCNICA ────────────────────────────────
 *
 * Es tentador subir la exigencia: fotos nítidas, bien iluminadas, todo más fácil de procesar.
 * Y sería un error, porque **un filtro estricto no atrapa a ningún defraudador**.
 *
 * El defraudador tiene tiempo, tiene luz y tiene un móvil bueno. Va a reintentar hasta que la
 * foto salga perfecta, porque le importa mucho pasar. El filtro no le molesta en absoluto.
 *
 * A quien deja fuera es a la señora de sesenta años con un teléfono de hace seis años, en una
 * habitación con una bombilla amarilla, intentando cobrar algo que necesita. Ella no reintenta
 * diez veces: lo intenta dos y abandona.
 *
 * **Cada punto que subimos el umbral es gente real que no puede verificarse, y ninguno de esos
 * es el que queríamos parar.**
 *
 * Por eso los umbrales de abajo son BAJOS: rechazan lo que de verdad no se puede leer, y nada
 * más. Que una foto mediocre pase y el OCR la lea regular es preferible a que una persona
 * honesta se quede fuera — y no es peligroso, porque el OCR tiene sus propias comprobaciones
 * (los dígitos verificadores del MRZ, ver `documents/mrz.ts`). Una lectura mala no se cuela en
 * silencio: se cae sola.
 */

export type EstadoLuz = 'ok' | 'oscura' | 'quemada';
export type EstadoNitidez = 'ok' | 'borrosa';

export interface Calidad {
  luz: EstadoLuz;
  nitidez: EstadoNitidez;
  /** 0-255. La media de luminancia. */
  brillo: number;
  /** Varianza del laplaciano. Más alto = más detalle. */
  detalle: number;
  /** Fracción de píxeles quemados (0-1). */
  quemados: number;
  sePuedeUsar: boolean;
}

/**
 * Debajo de esto no hay información que leer, por mucho que se procese.
 *
 * 40 sobre 255 es MUY oscuro — casi negro. No es "poca luz": es una foto en la que no se ve
 * el documento. Poner esto en 80 o 100 sería más cómodo para nosotros y dejaría fuera a media
 * ciudad de noche.
 */
export const BRILLO_MINIMO = 40;

/** Y por encima de esto la foto es un fogonazo blanco. */
export const BRILLO_MAXIMO = 235;

/**
 * Cuántos píxeles quemados se toleran.
 *
 * ── POR QUÉ ESTO NO SE MIDE CON LA MEDIA ───────────────────────────────────────
 *
 * Una foto con flash directo sobre el plástico del DNI tiene una media de brillo PERFECTA: el
 * reflejo blanco de un lado compensa la sombra del otro. La media dice "ok" y el dato está
 * ilegible debajo del brillo.
 *
 * Por eso se cuentan los píxeles reventados aparte. Es el caso más común de todos —todo el
 * mundo apunta el flash al DNI— y la media no lo ve.
 */
export const QUEMADOS_MAXIMO = 0.12;

/**
 * Debajo de esto la imagen no tiene bordes: está movida o desenfocada.
 *
 * 60 es tolerante a propósito. Una cámara barata con poco contraste da varianzas bajas aunque
 * la foto sea perfectamente legible.
 */
export const DETALLE_MINIMO = 60;

/** La media de luminancia. `grises` es un byte por píxel. */
export function medirBrillo(grises: Uint8Array): number {
  if (grises.length === 0) return 0;
  let suma = 0;
  for (const g of grises) suma += g;
  return suma / grises.length;
}

/** Qué fracción de la imagen está reventada de blanco. Ver `QUEMADOS_MAXIMO`. */
export function medirQuemados(grises: Uint8Array): number {
  if (grises.length === 0) return 0;
  let n = 0;
  for (const g of grises) if (g >= 250) n++;
  return n / grises.length;
}

/**
 * Varianza del laplaciano: la medida estándar de enfoque.
 *
 * El laplaciano resalta los bordes. Una imagen nítida tiene bordes marcados y por tanto mucha
 * varianza; una borrosa los tiene difuminados y la varianza se desploma. No hace falta saber
 * QUÉ hay en la foto para saber si tiene bordes.
 *
 * Se recorre el interior de la imagen (sin el marco de 1 píxel) porque el kernel necesita los
 * ocho vecinos. Los bordes de la foto no dicen nada del enfoque de todos modos.
 */
export function medirDetalle(grises: Uint8Array, ancho: number, alto: number): number {
  if (ancho < 3 || alto < 3 || grises.length < ancho * alto) return 0;

  const lap: number[] = [];
  for (let y = 1; y < alto - 1; y++) {
    for (let x = 1; x < ancho - 1; x++) {
      const i = y * ancho + x;
      // Kernel de 4 vecinos: centro * 4 menos arriba, abajo, izquierda y derecha.
      const v =
        4 * grises[i]! -
        grises[i - 1]! -
        grises[i + 1]! -
        grises[i - ancho]! -
        grises[i + ancho]!;
      lap.push(v);
    }
  }

  if (lap.length === 0) return 0;
  const media = lap.reduce((a, b) => a + b, 0) / lap.length;
  return lap.reduce((a, v) => a + (v - media) ** 2, 0) / lap.length;
}

/**
 * El veredicto de calidad.
 *
 * ── AQUÍ SÍ SE DICE QUÉ PASA, Y ES LA EXCEPCIÓN A LA REGLA ─────────────────────
 *
 * En la prueba de vida, el motivo del fallo NO se muestra: le enseñaría al atacante qué
 * ajustar. Aquí es al revés, y la diferencia está en quién puede accionar qué.
 *
 * "Hay poca luz" es algo que la persona puede arreglar en cinco segundos, y que a un
 * defraudador no le regala nada — él ya sabe iluminar. Callarlo solo dejaría a alguien
 * honesto mirando una pantalla que falla sin decir por qué.
 *
 * La regla no es "nunca digas nada": es **nunca digas nada que solo le sirva al que ataca**.
 */
export function evaluarCalidad(grises: Uint8Array, ancho: number, alto: number): Calidad {
  const brillo = medirBrillo(grises);
  const quemados = medirQuemados(grises);
  const detalle = medirDetalle(grises, ancho, alto);

  const luz: EstadoLuz =
    brillo < BRILLO_MINIMO
      ? 'oscura'
      : brillo > BRILLO_MAXIMO || quemados > QUEMADOS_MAXIMO
        ? 'quemada'
        : 'ok';

  const nitidez: EstadoNitidez = detalle < DETALLE_MINIMO ? 'borrosa' : 'ok';

  return { luz, nitidez, brillo, detalle, quemados, sePuedeUsar: luz === 'ok' && nitidez === 'ok' };
}

/**
 * Lo que se le dice a la persona mientras encuadra. Es la pastilla del Figma.
 *
 * Una instrucción a la vez. Decir "hay poca luz Y está borroso" a alguien que sostiene un DNI
 * con una mano y el móvil con la otra es no decirle nada: arregla una cosa cada vez.
 *
 * La luz va primero porque casi siempre arregla las dos: con más luz la cámara baja la
 * exposición, y con menos exposición hay menos movimiento borroso.
 */
export function pistaParaLaPersona(c: Calidad): string | null {
  if (c.luz === 'oscura') return 'Busca un sitio con más luz';
  if (c.luz === 'quemada') return 'Hay demasiado brillo. Evita la luz directa';
  if (c.nitidez === 'borrosa') return 'Mantén el pulso y espera a que enfoque';
  return null;
}
